import { useEffect, useRef, useState } from "react";
import { ScriptImage, SCRIPT_IMG } from "@/components/ScriptImage";
import SlideOverPanel from "@/components/SlideOverPanel";

const PASSWORD = "letsgetbaked";
const ILLUSTRATION = `${process.env.REACT_APP_BACKEND_URL}/api/illustrations/dresscode.png?v2`;
const TOKEN_KEY = "bakers_entourage_unlocked";

// Palette pulled from the watercolour: sage, mauve, coral, burgundy, cream.
const PALETTE = [
  { name: "Sage", hex: "#A8B98F" },
  { name: "Mauve", hex: "#B97A8E" },
  { name: "Coral", hex: "#D87878" },
  { name: "Burgundy", hex: "#76232F" },
  { name: "Cream", hex: "#FAF7F2" },
];

// Bridesmaids in mauve, groomsmen in blue.
const TAG_COLOURS = {
  bridesmaids: "#B97A8E",
  groomsmen: "#5B7AA6",
  bridal_party: "#76232F",
  all: "#A8B98F",
  vendors: "#D87878",
};

const TAG_LABELS = {
  bridesmaids: "Bride & Bridesmaids",
  groomsmen: "Groom & Groomsmen",
  bridal_party: "Bridal party",
  all: "All guests",
  vendors: "Vendors",
};

const SCHEDULE_SECTIONS = [
  {
    id: "welcome-dinner",
    title: "Welcome dinner",
    items: [
      {
        time: "6:00 pm",
        event: "Lucia Tapas",
        tag: "bridal_party",
        link: "https://luciatapas.com.au/",
        mapUrl: "https://www.google.com/maps/search/?api=1&query=Lucia+Tapas+Toowoomba",
      },
    ],
  },
  {
    id: "wedding-prep",
    title: "Wedding prep",
    items: [
      { time: "7:00 am", event: "Hair & make-up arrive on-site • bride and bridesmaids arrive on-site", tag: "bridesmaids" },
      { time: "9:00 am", event: "Brunch served in the wedding suite", tag: "bridesmaids" },
    ],
  },
  {
    id: "wedding-timeline",
    title: "Wedding timeline",
    items: [
      { time: "2:00 pm", event: "Groom and groomsmen arrive on-site", tag: "groomsmen" },
      { time: "2:30 pm", event: "Guests arrive on-site & are welcomed with a beverage", tag: "all" },
      // Ceremony & Cocktail Hour
      { time: "3:00 pm", event: "Ceremony", tag: "all" },
      { time: "3:45 pm", event: "Ceremony concludes • group / family photos • beverage service commences", tag: "all" },
      { time: "4:00 pm", event: "Canapés", tag: "all" },
      { time: "4:10 pm", event: "Cocktail round", tag: "all" },
      // Reception
      { time: "5:45 pm", event: "Guests enter reception", tag: "all" },
      { time: "6:15 pm", event: "Bridal party enters • welcome toast & grace", tag: "bridal_party" },
      { time: "6:30 pm", event: "Entrée served", tag: "all" },
      { time: "6:45 pm", event: "Speeches — part 1 (order: Maid of Honour, Best Man)", tag: "all" },
      { time: "7:30 pm", event: "Mains served", tag: "all" },
      { time: "7:45 pm", event: "Speeches — part 2 (order: Parents of the Bride, Parents of the Groom)", tag: "all" },
      { time: "7:50 pm", event: "Vendor meals served", tag: "vendors" },
      { time: "8:15 pm", event: "Cake cutting • father–daughter dance • first dance • dancefloor opens", tag: "all" },
      { time: "8:30 pm", event: "Cake plattered for self-serve", tag: "all" },
      // Conclusion
      { time: "10:30 pm", event: "Bus arrives on-site", tag: "all" },
      { time: "10:45 pm", event: "Beverage service & music conclude • bride and groom special exit • guests depart offsite", tag: "all" },
      { time: "11:00 pm", event: "Venue closes", tag: "all" },
    ],
  },
];

const CONTACTS = {
  "With Isabelle": {
    accent: "#B97A8E",
    people: [
      { name: "Sarah Chan", role: "Maid of Honour", phone: "0435 155 823" },
      { name: "Iris Cambel", role: "Bridesmaid", phone: "0456 004 110" },
      { name: "Stella Cambel", role: "Bridesmaid", phone: "+1 (773) 939-2806" },
      { name: "Elise Salisbury", role: "Bridesmaid", phone: "0407 574 586" },
    ],
  },
  "With Scott": {
    accent: "#5B7AA6",
    people: [
      { name: "Jake Lambert", role: "Best Man", phone: "0437 088 082" },
      { name: "Vidal Naidoo", role: "Groomsman", phone: "0401 788 723" },
      { name: "Armand Pienaar", role: "Groomsman", phone: "0401 235 371" },
      { name: "Clay Lambert", role: "Groomsman", phone: "0468 410 621" },
    ],
  },
  Vendors: {
    accent: "#D87878",
    people: [
      { name: "Madison", role: "Venue Coordinator", phone: "0408 930 206" },
    ],
  },
};


function WelcomeSplash({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const inputRef = useRef(null);

  // Match the main-site behaviour: the password field fades in slowly a
  // moment after the illustration has settled (3000ms ease-in, same as the
  // bakers gate).
  useEffect(() => {
    const t = setTimeout(() => setFormReady(true), 600);
    return () => clearTimeout(t);
  }, []);

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    const value = (inputRef.current?.value ?? password).trim().toLowerCase();
    if (value === PASSWORD) {
      setError(false);
      sessionStorage.setItem(TOKEN_KEY, "1");
      onSuccess();
    } else {
      setError(true);
    }
  };

  return (
    <div
      data-testid="entourage-welcome"
      className="min-h-screen flex flex-col items-center justify-center px-6 py-10"
      style={{
        background: "#F4EBDD",
        fontFamily: "'Cormorant Garamond', serif",
      }}
    >
      <img
        src={ILLUSTRATION}
        alt="The bridal party"
        data-testid="entourage-illustration"
        className="mx-auto w-full max-w-[300px] h-auto"
        loading="eager"
      />

      <form
        onSubmit={submit}
        className={`mt-10 flex flex-col items-center ${
          formReady ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{
          transition: "opacity 3000ms ease-in",
          willChange: "opacity",
        }}
        data-testid="entourage-password-form"
      >
        <input
          ref={inputRef}
          data-testid="entourage-password-input"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError(false);
          }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="password"
          aria-label="password"
          className="bg-transparent border-0 border-b border-[#76232F]/50 text-[#76232F] text-sm sm:text-base text-center placeholder:text-[#76232F]/35 focus:outline-none focus:border-[#76232F] px-2 py-1 w-[220px] tracking-[0.18em] lowercase"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        />
        <div
          data-testid="entourage-password-error"
          className={`mt-3 text-[11px] sm:text-xs uppercase tracking-[0.24em] transition-opacity duration-300 ${
            error ? "opacity-100" : "opacity-0"
          }`}
          style={{
            color: "#76232F",
            fontFamily: "'Cormorant Garamond', serif",
            minHeight: "1em",
          }}
          aria-live="polite"
        >
          invalid password
        </div>
        <button
          type="button"
          data-testid="entourage-password-submit"
          onClick={submit}
          className="mt-4 text-[11px] sm:text-xs uppercase tracking-[0.32em] text-[#76232F]/60 [@media(hover:hover)]:hover:text-[#76232F] active:text-[#76232F] transition-colors cursor-pointer"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            letterSpacing: "0.32em",
          }}
        >
          enter
        </button>
      </form>
    </div>
  );
}


function Accordion({ id, label, accent = "#76232F", isOpen, onToggle, level = 1, offsetClass = "", children }) {
  const sizeCls = level === 1
    ? "text-[14px] md:text-[18px]"
    : "text-[13px] md:text-[16px]";
  return (
    <div
      className={
        level === 1
          ? `border-b border-[#76232F]/15 ${offsetClass}`
          : "border-b border-[#76232F]/10"
      }
      data-testid={`accordion-${id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        data-testid={`accordion-toggle-${id}`}
        className="w-full flex items-center justify-between gap-6 py-4 text-left group"
      >
        <span
          className={`leading-tight ${sizeCls}`}
          style={{
            color: accent,
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            letterSpacing: "0.01em",
          }}
        >
          {label}
        </span>
        <span
          className={`shrink-0 text-[14px] transition-transform duration-300 ${
            isOpen ? "rotate-45" : "rotate-0"
          }`}
          style={{ color: accent }}
          aria-hidden
        >
          +
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pb-6">{children}</div>
        </div>
      </div>
    </div>
  );
}


// Flattened roster of every person, each tagged with their group's accent
// colour and role. Sorted alphabetically by first name. Used by both the
// Entourage roster (with roles) and the Contact list (with phones).
const ALL_PEOPLE = Object.entries(CONTACTS).flatMap(([group, { accent, people }]) =>
  people.map((p) => ({ ...p, group, accent }))
);
const ROSTER = ALL_PEOPLE
  .filter((p) => p.group !== "Vendors")
  .sort((a, b) => a.name.localeCompare(b.name));
// Madison (vendor) is pinned to the top of the contact list; then MOH and
// Best Man (lead roles), then bridesmaids and groomsmen are interleaved in
// the order defined above.
const VENDOR_PEOPLE = ALL_PEOPLE.filter((p) => p.group === "Vendors");
const BRIDES = CONTACTS["With Isabelle"].people.map((p) => ({
  ...p,
  group: "With Isabelle",
  accent: CONTACTS["With Isabelle"].accent,
}));
const GROOMS = CONTACTS["With Scott"].people.map((p) => ({
  ...p,
  group: "With Scott",
  accent: CONTACTS["With Scott"].accent,
}));
// First the lead roles, then alternate the remaining bridesmaids and groomsmen.
const LEADS = [BRIDES[0], GROOMS[0]];
const REST = [];
const restBrides = BRIDES.slice(1);
const restGrooms = GROOMS.slice(1);
const maxLen = Math.max(restBrides.length, restGrooms.length);
for (let i = 0; i < maxLen; i += 1) {
  if (restBrides[i]) REST.push(restBrides[i]);
  if (restGrooms[i]) REST.push(restGrooms[i]);
}
const CONTACTS_FLAT = [...VENDOR_PEOPLE, ...LEADS, ...REST];


function EntourageRoster() {
  // Split back into Bridal Party + Groom's Party groups, each
  // alphabetised by first name. Vendors live in the Contact list only.
  const groups = Object.entries(CONTACTS).filter(([g]) => g !== "Vendors");
  return (
    <div
      className="text-[15px] md:text-base leading-[1.7] text-[#2C1A1D]/85 space-y-8"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      {groups.map(([group, { accent, people }]) => {
        return (
          <div key={group}>
            <h4
              className="text-[14px] md:text-[18px] leading-tight mb-3"
              style={{
                color: accent,
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
            >
              {group}
            </h4>
            <ul>
              {people.map((p, idx) => (
                <li
                  key={`${group}-${idx}`}
                  className="flex items-baseline justify-between gap-4 border-b border-[#76232F]/10 py-2"
                >
                  <span className="text-[#2C1A1D]/85">{p.name}</span>
                  <span
                    className="italic sm:text-right"
                    style={{ color: accent }}
                  >
                    {p.role}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}


function ContactList() {
  return (
    <section
      data-testid="entourage-contact-list"
      className="text-[15px] md:text-base leading-[1.7] text-[#2C1A1D]/85"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      <p className="italic text-[#2C1A1D]/70 mb-6">
        Please contact Madison for on-the-day queries.
      </p>
      <ul>
        {CONTACTS_FLAT.map((p, idx) => (
          <li
            key={`${p.name}-${idx}`}
            className="flex items-baseline justify-between gap-4 border-b border-[#76232F]/10 py-2"
          >
            <span className="text-[#2C1A1D]/80">
              {p.name}
              {p.group === "Vendors" && p.role ? ` (${p.role})` : ""}
            </span>
            {p.phone ? (
              <a
                href={`tel:${p.phone.replace(/\s+/g, "")}`}
                className="italic text-[#2C1A1D]/80 hover:text-[#76232F] transition-colors"
                data-testid={`contact-phone-${idx}`}
              >
                {p.phone}
              </a>
            ) : (
              <span className="italic text-[#2C1A1D]/40">—</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}


function BridesmaidsContent() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <div
      className="text-[15px] md:text-base leading-[1.7] text-[#2C1A1D]/85"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      <div className="space-y-8">
        <img
          src={`${BACKEND}/api/illustrations/bridesmaids-board.png?v4`}
          alt="Bridesmaids palette and Iris dress reference"
          data-testid="bridesmaids-board"
          className="w-full h-auto"
          loading="lazy"
        />
        <img
          src={`${BACKEND}/api/illustrations/bridesmaids-silhouettes.png?v4`}
          alt="Bridesmaids silhouette examples"
          data-testid="bridesmaids-silhouettes"
          className="w-full h-auto"
          loading="lazy"
        />
        <img
          src={`${BACKEND}/api/illustrations/bridesmaids-dresses.png?v2`}
          alt="Additional bridesmaids dress references"
          data-testid="bridesmaids-dresses"
          className="w-full h-auto"
          loading="lazy"
        />
      </div>

      <ul className="mt-6 list-disc pl-5 marker:text-[#B97A8E]">
        <li>mismatched dresses</li>
        <li>floor length</li>
        <li>variety of silhouettes</li>
        <li>no cotton / jersey / knit fabrics</li>
        <li>no prints but textures yes !!!</li>
        <li>rich colours as pictured</li>
        <li>minimal gold jewellery</li>
        <li>
          more importantly &mdash; wear what you love and feel absolutely
          comfortable in <span aria-hidden>🩷</span>
        </li>
      </ul>
    </div>
  );
}


function GroomsmenContent() {
  return (
    <div
      className="text-[15px] md:text-base leading-[1.7] text-[#2C1A1D]/85"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      <p>
        Black tuxedos, white shirts, black bow ties, black dress shoes.
        Scott will share the suiting partner closer to fitting dates.
      </p>
    </div>
  );
}


function OutfitGuideContent() {
  const [openSub, setOpenSub] = useState(null);
  const toggle = (id) => setOpenSub((prev) => (prev === id ? null : id));

  return (
    <div>
      <Accordion
        id="bridesmaids"
        label="Bridesmaids"
        accent="#B97A8E"
        level={2}
        isOpen={openSub === "bridesmaids"}
        onToggle={() => toggle("bridesmaids")}
      >
        <BridesmaidsContent />
      </Accordion>
      <Accordion
        id="groomsmen"
        label="Groomsmen"
        accent="#5B7AA6"
        level={2}
        isOpen={openSub === "groomsmen"}
        onToggle={() => toggle("groomsmen")}
      >
        <GroomsmenContent />
      </Accordion>
    </div>
  );
}


function ScheduleContent() {
  const [openSub, setOpenSub] = useState(null);
  const toggle = (id) => setOpenSub((prev) => (prev === id ? null : id));

  return (
    <div>
      <p className="italic text-[#2C1A1D]/75 text-[15px] mb-6 leading-[1.7]">
        The Dairy, Ravensbourne
        <br />
        Friday, October 30, 2026
      </p>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.18em] mb-8">
        {Object.entries(TAG_LABELS).map(([key, label]) => (
          <span
            key={key}
            className="inline-flex items-center gap-2"
            style={{ color: TAG_COLOURS[key] }}
            data-testid={`schedule-legend-${key}`}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: TAG_COLOURS[key] }}
            />
            {label}
          </span>
        ))}
      </div>

      {SCHEDULE_SECTIONS.map((section) => (
        <Accordion
          key={section.id}
          id={section.id}
          label={section.title}
          level={2}
          isOpen={openSub === section.id}
          onToggle={() => toggle(section.id)}
        >
          <ul data-testid={`schedule-list-${section.id}`}>
            {section.items.map(({ time, event, tag, link, mapUrl }) => (
              <li
                key={`${section.id}-${time}-${event}`}
                className="grid grid-cols-[80px_1fr] gap-4 items-baseline border-b border-[#76232F]/10 py-3"
              >
                <span className="italic text-[#76232F] text-lg whitespace-nowrap">
                  {time}
                </span>
                <div className="flex flex-col gap-1">
                  <span className="text-[15px] text-[#2C1A1D]/85 leading-snug">
                    {event.split(" • ").map((line, i) => {
                      const capped = line.charAt(0).toUpperCase() + line.slice(1);
                      return (
                        <span key={i} className="block">
                          {link && i === 0 ? (
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="italic underline underline-offset-4 decoration-[#76232F]/40 hover:decoration-[#76232F] hover:text-[#76232F] transition-colors"
                              data-testid={`schedule-link-${section.id}`}
                            >
                              {capped}
                            </a>
                          ) : (
                            capped
                          )}
                        </span>
                      );
                    })}
                  </span>
                  {mapUrl && (
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] uppercase tracking-[0.24em] text-[#76232F] hover:opacity-70 self-start mt-1"
                      data-testid={`schedule-directions-${section.id}`}
                    >
                      Directions &rarr;
                    </a>
                  )}
                  <span
                    className="text-[10px] uppercase tracking-[0.2em]"
                    style={{ color: TAG_COLOURS[tag] }}
                  >
                    {TAG_LABELS[tag]}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </Accordion>
      ))}
      <p className="text-[12px] italic text-[#2C1A1D]/65 mt-5">
        Draft runsheet &mdash; times may shift slightly. We&rsquo;ll share
        the final version closer to the day.
      </p>
    </div>
  );
}


function PortalContent({ onLock }) {
  const [activePanel, setActivePanel] = useState(null);

  const MENU = [
    // Exact positions copied from the user-supplied reference image.
    { id: "entourage",    label: "Entourage",    top: "10%", left: "12%" },
    { id: "outfit-guide", label: "Outfit guide", top: "38%", left: "42%" },
    { id: "schedule",     label: "Schedule",     top: "60%", left: "75%" },
    { id: "contact-list", label: "Contact list", top: "86%", left: "32%" },
  ];

  const renderPanel = (id) => {
    switch (id) {
      case "entourage":
        return <EntourageRoster />;
      case "outfit-guide":
        return <OutfitGuideContent />;
      case "schedule":
        return <ScheduleContent />;
      case "contact-list":
        return <ContactList />;
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen px-6 py-12"
      style={{
        background: "#F4EBDD",
        fontFamily: "'Cormorant Garamond', serif",
      }}
    >
      <div className="max-w-[720px] mx-auto">
        {/* Header */}
        <div className="text-center pt-2 pb-10">
          <ScriptImage src={SCRIPT_IMG.welcome} alt="Welcome" />
          <p className="uppercase tracking-[0.5em] text-[10px] text-[#76232F]/70 mt-6">
            The Bakers Entourage
          </p>
        </div>

        {/* Menu — links scattered around the centre, slide-over panels */}
        <nav
          className="relative max-w-[340px] mx-auto h-[200px] md:h-[230px] my-6"
          data-testid="entourage-menu"
        >
          {MENU.map((item) => (
            <button
              key={item.id}
              type="button"
              data-testid={`entourage-menu-${item.id}`}
              onClick={() => setActivePanel(item.id)}
              style={{
                position: "absolute",
                top: item.top,
                left: item.left,
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
              className="text-[#76232F] text-[13px] md:text-[15px] leading-tight cursor-pointer whitespace-nowrap lowercase [@media(hover:hover)]:hover:opacity-60 transition-opacity"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="pt-12 pb-4 flex items-center justify-between text-[11px] uppercase tracking-[0.32em] text-[#76232F]/60">
          <a
            href="/"
            className="hover:text-[#76232F] transition-colors"
            data-testid="entourage-back-link"
          >
            ← Main site
          </a>
          <button
            type="button"
            onClick={onLock}
            className="hover:text-[#76232F] transition-colors"
            data-testid="entourage-lock-btn"
          >
            Lock portal
          </button>
        </div>
      </div>

      <SlideOverPanel
        open={Boolean(activePanel)}
        title=""
        onClose={() => setActivePanel(null)}
      >
        {activePanel && renderPanel(activePanel)}
      </SlideOverPanel>
    </div>
  );
}


export default function EntouragePage() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(TOKEN_KEY) === "1") {
      setUnlocked(true);
    }
  }, []);

  if (!unlocked) {
    return <WelcomeSplash onSuccess={() => setUnlocked(true)} />;
  }
  return (
    <PortalContent
      onLock={() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setUnlocked(false);
      }}
    />
  );
}
